import React, { useCallback, useEffect, useState, useRef } from "react"
import ResizeSensor from "css-element-queries/src/ResizeSensor"

import RunsContainer from "../../../components/graphing/RunsContainer"
import { getFormattedMessage, isLTPM } from "../../../components/shared_components_registration"
import ShowPlaceholdersToggle from "./TabContent/ShowPlaceholdersToggle"
import DayCutoffFilter from "./TabContent/DayCutoffFilter"
import PerformanceMetricsPanel from "./PerformanceMetricsPanel"
import RunComments from "./RunComments"
import LiveReactorSelector from "./LiveReactorSelector"

/*
  Function props passed to RunsContainer are cached via useCallback
  to prevent unnecessary re-renders of RunsContainer when this component re-renders.
*/

export default function LTPMDash({
  intl,
  runQuery = {},
  updateRunQuery,
  run,
  actions,
  isQueryingRunIDs,
  commentsLoading,
  runs,
  setRunFocus,
  setCommentsLoading,
  canTogglePlaceholders = true,
  applicationRunsProps = {},
}) {
  const { errMsg, reactorSelectorProps = {}, ...appProps } = applicationRunsProps

  const [plotList, setPlotListState] = useState([])
  const setPlotList = useCallback((plots) => {
    setPlotListState(plots)
  }, [])

  const reloadRun = useCallback((passed_run) => {
    if (passed_run && passed_run.forceReload) {
      setCommentsLoading(true)
      passed_run.forceReload()
    }
  }, [])

  const [loadedRuns, setLoadedRuns] = useState(runs)
  const [runsAvailable, setRunsAvailable] = useState(runs && runs.length > 0)
  useEffect(() => {
    setLoadedRuns(runs)
    setRunsAvailable(runs && runs.length > 0)
  }, [runs])

  // Keep track of state of whether tags data are loading or not
  const [tagsLoading, setTagsLoadingState] = useState(runsAvailable)
  const setTagsLoading = useCallback(
    (load) => {
      load ? !tagsLoading && setTagsLoadingState(true) : tagsLoading && setTagsLoadingState(false)
    },
    [tagsLoading],
  )

  // Handling collapsible right-side menu
  const [collapseMenu, setCollapseMenu] = useState(false)
  const [leftSectionWidth, setLeftSectionWidth] = useState(0)
  const [isSmallScreen, setIsSmallScreen] = useState(false)
  const leftSectionRef = useRef(null)

  const element = document.getElementById("live-dash-left-section")

  const toggleCollapseMenu = () => {
    setCollapseMenu(!collapseMenu)
  }

  useEffect(() => {
    if (element) {
      new ResizeSensor(element, () => setLeftSectionWidth(element.getBoundingClientRect().width))
      setLeftSectionWidth(element.getBoundingClientRect().width)
    }
  }, [element])

  useEffect(() => {
    if (leftSectionRef.current) {
      leftSectionRef.current.style.left = `${leftSectionWidth - (isLTPM ? -34 : 15)}px`
    }
  }, [leftSectionWidth])

  useEffect(() => {
    if (window.innerWidth <= 1200) {
      // If breakpoint is met, un-collapse menu and remove option to collapse
      setIsSmallScreen(true)
      setCollapseMenu(false)
      leftSectionRef.current.style.display = "none"
    } else {
      setIsSmallScreen(false)
      leftSectionRef.current.style.display = "flex"
    }
  }, [window.innerWidth])
  return (
    <div className={"row full-height"}>
      <div
        id="live-dash-right-section-collapse"
        className={isSmallScreen ? "hide-right-panel-toggle" : ""}
        onClick={toggleCollapseMenu}
        title={
          !collapseMenu
            ? getFormattedMessage("general.collapse.menu", "Collapse Menu", true, intl)
            : ("general.show.menu", "Show Menu", true, intl)
        }
        ref={leftSectionRef}
      >
        {leftSectionWidth && element && (
          <span className={`fa fa-chevron-${collapseMenu ? "left" : "right"}`} />
        )}
      </div>
      <div
        className={`full-height ${collapseMenu ? "col-xl-12" : "col-xl-9"}`}
        style={{ padding: "5px" }}
        id={"live-dash-left-section"}
      >
        <div className={"content-block full-height-min"}>
          {errMsg && (
            <div
              className={"warning-message"}
              style={{ paddingBottom: "15px", paddingLeft: "5px", borderLeft: "solid", marginBottom: "3px" }}
            >
              {errMsg}
            </div>
          )}
          <RunsContainer
            intl={intl}
            live={true}
            runs={loadedRuns}
            isQueryingRunIDs={isQueryingRunIDs}
            setRunFocus={setRunFocus}
            setPlotList={setPlotList}
            setTagsLoading={setTagsLoading}
            tagsLoading={tagsLoading}
            live_run_cutoff={runQuery.cutoff}
            reloadRun={reloadRun}
            leftSectionWidth={leftSectionWidth - 60}
            RunFilteringWidgets={({ runsLoading = true, tagsLoading = true }) => {
              const { disabled, ...reactorSelector } = reactorSelectorProps
              const queryingActive = isQueryingRunIDs || runsLoading || tagsLoading
              if (errMsg) return <></>
              return (
                <>
                  {reactorSelector && (
                    <LiveReactorSelector
                      showModal={false}
                      showBtn={true}
                      hideGraphSelector={true}
                      btnDisabled={queryingActive || disabled}
                      {...reactorSelector}
                    />
                  )}
                  {canTogglePlaceholders && (
                    <ShowPlaceholdersToggle
                      intl={intl}
                      viewPlaceholders={runQuery.placeholder}
                      togglePlaceholders={() => updateRunQuery("placeholder", !runQuery.placeholder)}
                      disabled={queryingActive || disabled}
                    />
                  )}
                  <DayCutoffFilter
                    intl={intl}
                    cutoff={runQuery.cutoff}
                    loading={queryingActive}
                    disabled={disabled}
                    setCutoff={(days) => updateRunQuery("cutoff", days)}
                  />
                </>
              )
            }}
            {...appProps} // Application specific props - Some could be cleaned/generalized more probably
          />
        </div>
      </div>
      {!collapseMenu && (
        <div className={"col-xl-3 full-height"} style={{ padding: "5px" }} id={"live-dash-right-section"}>
          <div className={"half-height"} id={"live-performance-metrics"}>
            <div className={"content-block full-height"} style={{ padding: "5px" }}>
              <PerformanceMetricsPanel
                run={run}
                runsAvailable={runsAvailable}
                plotList={plotList}
                intl={intl}
              />
            </div>
          </div>
          <div className={"half-height"} id={"live-run-comments"}>
            <div className={"content-block full-height"} style={{ padding: "5px" }}>
              <RunComments
                run={run}
                actions={actions}
                loading={commentsLoading}
                runsAvailable={runsAvailable}
                intl={intl}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
